/**
 * Film files for the DTF film pages (Film Printing and Film Pre-print).
 *
 * `filmDropArgs` is the ONE builder of the agent drop payload, so both pages
 * drop (and download) under exactly the same `<id>__<name>` filename. The id
 * prefix lets two films that share an artwork SKU land side by side in the flat
 * RIP folder instead of overwriting each other.
 *
 * `downloadFilm` is the "Download film" action for a film whose drop did not
 * happen (the pages reach it through `useFilmDownload`, which owns the
 * single-flight guard). There is deliberately no stored payload: both prepare mutations run
 * with `gcTime: 0`, so the browser keeps no film bytes after a click, and each
 * click runs exactly one prepare. That is the same cost and the same safety as
 * Resend: `filmPrint.prepareFilm` reuses the existing PRINTING film row and
 * skips the shelf claim on a resend, and `filmPreprint.prepareStockFilm` only
 * reads and composites.
 */
import { downloadBlob } from '@/lib/download';

/** The prepare-result fields the builder reads (both prepare procedures carry them). */
export interface FilmPrepLike {
  compositedBytesBase64?: string | null;
  compositedFilename?: string | null;
  presignedUrl?: string | null;
  /** Film Printing only: the presigned file already carries the composited header. */
  presignedComposited?: boolean | null;
  filename: string;
}

export interface FilmDropArgs {
  filename: string;
  bytesBase64?: string;
  presignedUrl?: string;
}

/**
 * The agent drop payload for one film: composited bytes first, else the
 * presigned URL (under the composited name only when that upload is the
 * composited file), else null (nothing to drop).
 */
export function filmDropArgs(id: string, prep: FilmPrepLike): FilmDropArgs | null {
  if (prep.compositedBytesBase64 && prep.compositedFilename) {
    return { filename: `${id}__${prep.compositedFilename}`, bytesBase64: prep.compositedBytesBase64 };
  }
  if (prep.presignedUrl) {
    // Oversize composites are uploaded server-side and streamed by the agent:
    // the presigned file already carries the header, so it keeps the
    // COMPOSITED name.
    const name = prep.presignedComposited && prep.compositedFilename ? prep.compositedFilename : prep.filename;
    return { filename: `${id}__${name}`, presignedUrl: prep.presignedUrl };
  }
  return null;
}

export type DownloadFilmResult = 'saved' | 'opened' | 'blocked' | 'unavailable';

export interface DownloadFilmDeps {
  /** Runs the page's prepare mutation once. */
  prepare: () => Promise<FilmPrepLike>;
  saveBlob?: (blob: Blob, filename: string) => void;
  /** Opens a new tab. Must NOT pass 'noopener': with it window.open always returns null and a blocked tab is undetectable. */
  openUrl?: (url: string) => Window | null;
}

function decodeBase64(b64: string): Uint8Array<ArrayBuffer> {
  const bin = atob(b64);
  const bytes = new Uint8Array(new ArrayBuffer(bin.length));
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return bytes;
}

/**
 * Download one film: bytes are saved as `<id>__<name>`; a storage URL (always
 * freshly minted by this prepare) opens in a new tab. A rejected prepare
 * rejects to the caller.
 */
export async function downloadFilm(id: string, deps: DownloadFilmDeps): Promise<DownloadFilmResult> {
  const prep = await deps.prepare();
  const args = filmDropArgs(id, prep);
  if (!args) return 'unavailable';
  if (args.bytesBase64) {
    (deps.saveBlob ?? downloadBlob)(new Blob([decodeBase64(args.bytesBase64)]), args.filename);
    return 'saved';
  }
  const opened = (deps.openUrl ?? ((url: string) => window.open(url, '_blank')))(args.presignedUrl!);
  // The tab opens after an await, outside the click, so a popup blocker can stop it.
  if (!opened) return 'blocked';
  opened.opener = null;
  return 'opened';
}
